/**
 * Deterministic pseudo-random number generation.
 *
 * The universe is a pure function of the world seed, so `Math.random()` is
 * unusable here — it cannot be seeded, and two servers reconstructing the same
 * system have to agree exactly.
 *
 * Two well-known 32-bit algorithms, chosen because they are small enough to
 * read in full and produce identical output on every JS engine (`Math.imul`
 * and the bitwise operators are all exactly specified):
 *
 *   xmur3      — string -> 32-bit seed
 *   mulberry32 — 32-bit seed -> uniform stream
 *
 * Neither is cryptographically secure. Nothing here needs that; anything
 * security-sensitive must not come from this file.
 */
/** Hash an arbitrary string into a well-distributed 32-bit seed. */
export function hashSeed(input) {
    let h = 1779033703 ^ input.length;
    for (let i = 0; i < input.length; i++) {
        h = Math.imul(h ^ input.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
}
/**
 * A seeded stream of pseudo-random values.
 *
 * Instances are stateful: each draw advances the stream. Two instances built
 * from the same seed produce the same sequence.
 */
export class Rng {
    #state;
    constructor(seed) {
        this.#state = typeof seed === 'string' ? hashSeed(seed) : seed >>> 0;
    }
    /**
     * Derive an independent stream for a named entity.
     *
     * Each generated thing gets its own stream keyed by its full identity path,
     * rather than drawing from a shared per-system stream. That costs a hash per
     * entity and buys stability: adding a field to planet generation cannot shift
     * the stations, because they were never reading the same stream.
     */
    static forEntity(...parts) {
        return new Rng(parts.join(':'));
    }
    /** Uniform in [0, 1). */
    float() {
        this.#state = (this.#state + 0x6d2b79f5) | 0;
        let t = Math.imul(this.#state ^ (this.#state >>> 15), 1 | this.#state);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }
    /** Uniform in [min, max]. Both ends inclusive. */
    int(min, max) {
        if (max < min)
            throw new RangeError(`Empty range: int(${min}, ${max})`);
        return min + Math.floor(this.float() * (max - min + 1));
    }
    /** Uniform in [min, max). */
    range(min, max) {
        return min + this.float() * (max - min);
    }
    /** True with probability `p`. */
    bool(p = 0.5) {
        return this.float() < p;
    }
    /** A uniformly chosen element. Throws on an empty list rather than returning undefined. */
    pick(items) {
        if (items.length === 0)
            throw new RangeError('Cannot pick from an empty list');
        const item = items[this.int(0, items.length - 1)];
        if (item === undefined)
            throw new RangeError('Cannot pick from a sparse list');
        return item;
    }
    /**
     * Choose by relative weight. Weights need not sum to 1; non-positive weights
     * are never chosen.
     */
    weighted(entries) {
        let total = 0;
        for (const [, weight] of entries) {
            if (weight > 0)
                total += weight;
        }
        if (total <= 0)
            throw new RangeError('No entry has positive weight');
        let roll = this.float() * total;
        for (const [value, weight] of entries) {
            if (weight <= 0)
                continue;
            roll -= weight;
            if (roll < 0)
                return value;
        }
        // Only reachable through floating-point drift on the final entry.
        return entries[entries.length - 1][0];
    }
    /** Fisher-Yates, on a copy. */
    shuffle(items) {
        const out = [...items];
        for (let i = out.length - 1; i > 0; i--) {
            const j = this.int(0, i);
            [out[i], out[j]] = [out[j], out[i]];
        }
        return out;
    }
}
//# sourceMappingURL=rng.js.map